import React, { useState, useEffect, useCallback } from "react";
import { Shield, Plus, Trash2, Save, Search, Lock } from "lucide-react";
import NoDataFound from "../common/NoDataFound";
import rolesService, {
  Activity,
  ActivityExtent,
  AdminRole,
  MODULE_NAMES,
  ACTIVITY_EXTENT_LABELS,
  RoleActivityAssignment,
} from "../../services/rolesService";
import FormControl from "../common/FormControl";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Pagination from "../common/Pagination";
import Tip from "../common/Tip";
import Toast, { ToastMessage } from "../common/Toast";
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import { confirmWarning, confirmActive } from "../common/confirm";
import Skeleton from "../common/Skeleton";

interface AdminRoleManagementProps {
  compact?: boolean;
}

// Left-nav role list pages once it grows past this — test/seed roles can run
// into the hundreds and bury the real ones in an un-navigable scroll.
const ROLE_PAGE_SIZE = 12;

const AdminRoleManagementLive: React.FC<AdminRoleManagementProps> = ({
  compact = false,
}) => {
  // Role↔user assignment is gated by the ORG_RBAC activity (role DEFINITION is
  // super-admin-only and lives elsewhere; this component only assigns users).
  const { allowed: canModify, loading: permLoading } = usePermission('ORG_RBAC', 'view+modify');
  const { formatDate } = useEntitySettings();
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [totalRoles, setTotalRoles] = useState(0);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [allUsers, setAllUsers] = useState<
    { id: number; name: string; email: string }[]
  >([]);
  const [selectedRole, setSelectedRole] = useState<AdminRole | null>(null);
  const [roleSearch, setRoleSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Pagination — the role list paginates SERVER-SIDE (page/limit/search go to
  // the API, `total` drives the pager), so every role stays reachable no matter
  // how many exist. Activities and user-assignments inside the selected role
  // still paginate client-side (bounded, small sets).
  const [rolesPage, setRolesPage] = useState(1);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Add-user modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState("");
  const [effectiveDateToAdd, setEffectiveDateToAdd] = useState("");
  const [notesToAdd, setNotesToAdd] = useState("");

  // Debounce the search box → server query; snap back to page 1 on every change.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(roleSearch.trim());
      setRolesPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [roleSearch]);

  // Fetch the current page of roles (server-side filter + pagination).
  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      // BR5: this entity-side RBAC tab is the Entity Admin's role-ASSIGNMENT
      // surface. The "Entity Administrator" role IS assignable here — an EA may
      // delegate it to others. All active roles are shown; the server enforces
      // the remaining boundaries (e.g. a root EA can only be un-made by an SA).
      const { data, total } = await rolesService.getRoles({
        page: rolesPage,
        limit: ROLE_PAGE_SIZE,
        search: debouncedSearch || undefined,
      });
      setRoles(data);
      setTotalRoles(total);
      // Refresh the open role's detail if it sits on the page we just loaded.
      setSelectedRole((prev) =>
        prev ? data.find((r) => r.id === prev.id) ?? prev : prev,
      );
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.response?.data?.detail || err.message || "Failed to load roles",
      });
    } finally {
      setLoading(false);
    }
  }, [rolesPage, debouncedSearch]);

  // Activities catalogue + entity users load once (not paginated here).
  const loadMeta = useCallback(async () => {
    try {
      const [activitiesData, usersData] = await Promise.all([
        rolesService.getActivities(),
        rolesService.getUsersDropdown(),
      ]);
      setAllActivities(activitiesData);
      setAllUsers(usersData);
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.response?.data?.detail || err.message || "Failed to load data",
      });
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadRoles(); }, [loadRoles]);

  useEffect(() => {
    setActivitiesPage(1);
    setUsersPage(1);
  }, [selectedRole?.id]);

  const handleSelectRole = (role: AdminRole) => {
    setSelectedRole(role);
  };

  // Re-fetch the selected role's enriched detail after a mutation — it may not
  // be on the visible page, so query it by name and match on id.
  const refreshSelectedRole = useCallback(async (role: AdminRole) => {
    try {
      const { data } = await rolesService.getRoles({
        search: role.role_name,
        limit: ROLE_PAGE_SIZE,
      });
      const fresh = data.find((r) => r.id === role.id);
      if (fresh) setSelectedRole(fresh);
    } catch {
      /* non-fatal — keep the existing detail rather than blanking the panel */
    }
  }, []);

  const openAddUser = () => {
    setSelectedUserToAdd("");
    setEffectiveDateToAdd("");
    setNotesToAdd("");
    setShowAddUserModal(true);
  };

  const handleAddUser = async () => {
    if (!selectedRole || !selectedUserToAdd) return;
    setSubmitting(true);
    try {
      const role = selectedRole;
      await rolesService.assignUserToRole(
        role.id,
        parseInt(selectedUserToAdd),
        effectiveDateToAdd || null,
        notesToAdd.trim() || null,
      );
      setShowAddUserModal(false);
      await loadRoles();
      await refreshSelectedRole(role);
      setToast({ type: "success", message: "User assigned to role." });
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.response?.data?.detail || "Failed to assign user",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (!selectedRole) return;
    const user = allUsers.find((u) => u.id === userId);
    const ok = await confirmWarning({
      title: "Remove this user from role?",
      text: user
        ? `${user.name} will no longer have this role in this entity.`
        : "User will no longer have this role in this entity.",
      confirmText: "Yes, Remove",
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      const role = selectedRole;
      await rolesService.deactivateUserFromRole(role.id, userId);
      await loadRoles();
      await refreshSelectedRole(role);
      setToast({ type: "success", message: "User removed from role." });
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.response?.data?.detail || "Failed to remove user",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const activitiesById = new Map<number, Activity>(
    allActivities.map((a) => [a.id, a]),
  );

  const displayRole = selectedRole;
  const displayRoleAssignments: RoleActivityAssignment[] =
    displayRole?.assigned_activities || [];
  const displayAssignments = displayRole?.assignments || [];

  const assignedActivityRows: {
    activity: Activity;
    extent: ActivityExtent;
    is_active: number;
  }[] = displayRoleAssignments
    .map((ra) => {
      const a = activitiesById.get(ra.activity_id);
      return a
        ? { activity: a, extent: ra.extent, is_active: ra.is_active ?? 1 }
        : null;
    })
    .filter(
      (x): x is { activity: Activity; extent: ActivityExtent; is_active: number } =>
        Boolean(x),
    );

  const activeUserAssignments = displayAssignments.filter((a) => a.is_active === 1);

  const availableUsers = allUsers.filter(
    (u) => !displayAssignments.some((a) => a.user_id === u.id && a.is_active === 1),
  );

  const paginatedActivityRows = assignedActivityRows.slice(
    (activitiesPage - 1) * pageSize,
    activitiesPage * pageSize,
  );
  const paginatedUserAssignments = activeUserAssignments.slice(
    (usersPage - 1) * pageSize,
    usersPage * pageSize,
  );

  const extentPillClass = (e: ActivityExtent) =>
    e === "view"
      ? "bg-slate-100 text-slate-600"
      : e === "view+modify"
        ? "bg-amber-50 text-amber-700"
        : "bg-emerald-50 text-emerald-700";

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left nav card — read-only role list ─────────────────── */}
      {/* Fixed-zone layout: heading + search pinned to the top, the record
          list is the only scrolling region, pagination pinned to the bottom. */}
      <div className="netbody-card side-nav-card side-nav-card--fixed">
        <div className="side-nav-card-heading">
          <span>RBAC Roles</span>
        </div>
        <div className="side-nav-card-search">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
              placeholder="Search roles…"
              className="w-full text-xs rounded-md border border-slate-200 bg-white pl-7 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              data-testid="INP_admin_role_search"
            />
          </div>
        </div>
        <nav aria-label="RBAC roles">
          {loading &&
            // Mirror the real row geometry: shield icon · role name
            // (width varies per row so it reads like real data, not stripes)
            // · trailing count chip.
            [0, 1, 2, 3, 4, 5].map((i) => {
              const nameWidths = ["72%", "55%", "84%", "48%", "66%", "60%"];
              return (
                <div
                  key={i}
                  className="side-panel-menu-item"
                  style={{ pointerEvents: "none" }}
                  aria-hidden="true"
                >
                  <Skeleton.Circle size={14} />
                  <Skeleton.Box
                    width={nameWidths[i]}
                    height={11}
                    style={{ flex: "0 1 auto" }}
                  />
                  <Skeleton.Circle size={18} style={{ marginLeft: "auto" }} />
                </div>
              );
            })}

          {!loading && roles.length === 0 && !debouncedSearch && (
            <NoDataFound
              title="No roles"
              message="No roles defined yet."
              icon={<Shield size={28} />}
            />
          )}
          {!loading && roles.length === 0 && debouncedSearch && (
            <p className="px-3 py-4 text-center text-xs text-slate-400">
              No roles match “{debouncedSearch}”.
            </p>
          )}

          {!loading &&
            roles.map((role) => {
              const active = selectedRole?.id === role.id;
              const userCount = (role.assignments || []).filter(
                (a) => a.is_active === 1,
              ).length;
              return (
                <button
                  key={role.id}
                  onClick={() => handleSelectRole(role)}
                  className={`side-panel-menu-item${active ? " side-panel-menu-item--active" : ""}`}
                  data-testid={`NAV_admin_role_item_${role.id}`}
                  title={role.role_name}
                >
                  <Shield size={14} style={{ flexShrink: 0 }} />
                  <span
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "left",
                    }}
                  >
                    {role.role_name}
                  </span>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 600,
                      minWidth: 20,
                      textAlign: "center",
                      padding: "1px 6px",
                      borderRadius: 9999,
                      background: active
                        ? "var(--interactive-primary-bg)"
                        : "var(--border-subtle)",
                      color: active ? "#fff" : "var(--text-tertiary)",
                    }}
                    title={`${userCount} user${userCount === 1 ? "" : "s"}`}
                  >
                    {userCount}
                  </span>
                </button>
              );
            })}
        </nav>
        {totalRoles > ROLE_PAGE_SIZE && (
          <div className="side-nav-card-footer">
            <Pagination
              page={rolesPage}
              pageSize={ROLE_PAGE_SIZE}
              totalRecords={totalRoles}
              onPageChange={setRolesPage}
              recordLabel="roles"
            />
          </div>
        )}
      </div>

      {/* ── Right panel ─────────────────────────────────────────── */}
      {/* Symmetric fixed-zone: heading pinned to top, only the body scrolls. */}
      <div className="netbody-card side-content-card side-content-card--fixed flex flex-col flex-1 min-w-0">
        {!selectedRole ? (
          <div className="netbody-content">
            <div className="scroll-container-nofilters">
              <div className="flex flex-col items-center justify-center h-full text-slate-400 border rounded-lg bg-slate-50 border-dashed py-5">
                <Shield size={64} className="mb-4 opacity-20" />
                <h3 className="text-lg font-medium text-slate-600">
                  Select a Role
                </h3>
                <p className="text-sm max-w-xs text-center mt-2">
                  Choose a role from the left to view its definition and manage
                  user assignments for this entity.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="side-content-card-heading">
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <Shield size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={selectedRole.role_name}
                >
                  {selectedRole.role_name}
                </span>
              </span>
              {selectedRole.role_description && (
                <span className="text-xs font-normal text-slate-500 ml-2">
                  {selectedRole.role_description}
                </span>
              )}
            </div>

            <div className="netbody-content">
              <div className="scroll-container flex flex-col gap-y-6 p-1">

                {/* ── Role definition strip (read-only) ────────── */}
                <div className="flex flex-col gap-y-3">
                  {/* <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Role Definition
                  </h4>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex flex-col gap-y-1">
                    <span className="text-sm font-semibold text-slate-800">
                      {selectedRole.role_name}
                    </span>
                    {selectedRole.role_description ? (
                      <span className="text-xs text-slate-500">
                        {selectedRole.role_description}
                      </span>
                    ) : (
                      <span className="text-xs italic text-slate-400">
                        No description provided.
                      </span>
                    )}
                  </div> */}

                  {/* Allowed activities */}
                  <div className="flex flex-col gap-y-1">
                    {/* <span className="text-xs font-medium text-slate-500">
                      Allowed Activities ({assignedActivityRows.length})
                    </span> */}
                    <div className="table-list" style={{ height: "auto" }}>
                      <div className="table-list-scroll" style={{ flex: "0 0 auto" }}>
                        <table className="table-list-table">
                          <thead>
                            <tr>
                              <th className="table-list-th table-list-cell--sno">
                                S.No
                              </th>
                              <th className="table-list-th">Activity</th>
                              <th className="table-list-th">Module</th>
                              <th className="table-list-th">Extent</th>
                            </tr>
                          </thead>
                          <tbody aria-busy={loading} aria-live="polite">
                            {loading && (
                              <Skeleton.TableRows
                                cols={["3xs", "md", "sm", "pill"]}
                              />
                            )}
                            {!loading && assignedActivityRows.length === 0 && (
                              <tr>
                                <td colSpan={4} className="table-list-cell">
                                  <NoDataFound
                                    title="No Activities Assigned"
                                    message="No activities have been assigned to this role yet."
                                  />
                                </td>
                              </tr>
                            )}
                            {!loading &&
                              paginatedActivityRows.map(
                                ({ activity, extent }, idx) => (
                                  <tr
                                    key={activity.id}
                                    className="table-list-row"
                                  >
                                    <td className="table-list-cell table-list-cell--sno">
                                      {(activitiesPage - 1) * pageSize +
                                        idx +
                                        1}
                                    </td>
                                    <td className="table-list-cell">
                                      <span className="table-list-cell-title">
                                        {activity.activity_name}
                                      </span>
                                    </td>
                                    <td className="table-list-cell">
                                      {activity.module_code
                                        ? MODULE_NAMES[activity.module_code] ||
                                          activity.module_code
                                        : "—"}
                                    </td>
                                    <td className="table-list-cell">
                                      <span
                                        className={
                                          "px-2 py-0.5 rounded-full text-[11px] font-medium " +
                                          extentPillClass(extent)
                                        }
                                      >
                                        {ACTIVITY_EXTENT_LABELS[extent]}
                                      </span>
                                    </td>
                                  </tr>
                                ),
                              )}
                          </tbody>
                        </table>
                      </div>
                      {assignedActivityRows.length > 0 && (
                        <Pagination
                          page={activitiesPage}
                          pageSize={pageSize}
                          totalRecords={assignedActivityRows.length}
                          onPageChange={setActivitiesPage}
                          onPageSizeChange={(size) => { setPageSize(size); setActivitiesPage(1); }}
                          recordLabel="activities"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* ── User assignments ─────────────────────────── */}
                <div className="flex flex-col gap-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Assigned Users ({activeUserAssignments.length})
                    </h4>
                    <Button
                      variant="fill"
                      icon={Plus}
                      onClick={openAddUser}
                      disabled={submitting || permLoading || !canModify}
                      title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                      data-testid="BTN_admin_role_user_create"
                    >
                     Create
                    </Button>
                  </div>

                  <div className="table-list" style={{ height: "auto" }}>
                    <div className="table-list-scroll" style={{ flex: "0 0 auto" }}>
                      <table className="table-list-table">
                        <thead>
                          <tr>
                            <th className="table-list-th table-list-cell--sno">
                              S.No
                            </th>
                            <th className="table-list-th">User</th>
                            <th className="table-list-th">Effective Date</th>
                            <th className="table-list-th">Notes</th>
                            <th className="table-list-th table-list-th--right">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                          {loading && (
                            <Skeleton.TableRows
                              cols={[
                                "3xs",
                                "md",
                                "sm",
                                "md",
                                { width: "icon", align: "right", count: 1 },
                              ]}
                            />
                          )}
                          {!loading && activeUserAssignments.length === 0 && (
                            <tr>
                              <td colSpan={5} className="table-list-cell">
                                <NoDataFound
                                  title="No Users Assigned"
                                  message='No users have been assigned to this role yet. Click "Add User" to assign one.'
                                />
                              </td>
                            </tr>
                          )}
                          {!loading &&
                            paginatedUserAssignments.map((assignment, idx) => (
                              <tr
                                key={assignment.id}
                                className="table-list-row"
                              >
                                <td className="table-list-cell table-list-cell--sno">
                                  {(usersPage - 1) * pageSize + idx + 1}
                                </td>
                                <td className="table-list-cell">
                                  <span className="table-list-cell-title">
                                    {assignment.user_name}
                                  </span>
                                </td>
                                <td className="table-list-cell">
                                  {assignment.effective_date
                                    ? formatDate(assignment.effective_date)
                                    : <span className="italic text-slate-400">—</span>}
                                </td>
                                <td className="table-list-cell">
                                  {assignment.notes || (
                                    <span className="italic text-slate-400">—</span>
                                  )}
                                </td>
                                <td className="table-list-cell table-list-cell--actions">
                                  {assignment.is_root_admin ? (
                                    <Tip
                                      text="System Admin–granted root Entity Administrator — locked, cannot be removed"
                                      placement="left"
                                    >
                                      <span
                                        className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
                                        aria-label="System role, locked"
                                        data-testid={`BADGE_admin_role_user_row_${assignment.id}_system_locked`}
                                      >
                                        <Lock size={12} /> System
                                      </span>
                                    </Tip>
                                  ) : (
                                    <Tip text="Remove user" placement="left">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveUser(assignment.user_id);
                                        }}
                                        disabled={submitting}
                                        className="table-list-action table-list-action--delete"
                                        aria-label="Remove user from role"
                                        data-testid={`BTN_admin_role_user_row_${assignment.id}_delete`}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </Tip>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {activeUserAssignments.length > 0 && (
                      <Pagination
                        page={usersPage}
                        pageSize={pageSize}
                        totalRecords={activeUserAssignments.length}
                        onPageChange={setUsersPage}
                        onPageSizeChange={(size) => { setPageSize(size); setUsersPage(1); }}
                        recordLabel="users"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Add User modal ───────────────────────────────────────── */}
      <Modal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        size="sm"
      >
        <Modal.Header>
          Assign User{selectedRole ? ` · ${selectedRole.role_name}` : ""}
        </Modal.Header>
        <Modal.Body>
          <form
            id="add-user-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleAddUser();
            }}
            className="flex flex-col gap-y-4"
            data-testid="MOD_admin_role_user_form"
          >
            <FormControl.SearchSelect
              label="User"
              options={availableUsers.map((u) => ({
                value: String(u.id),
                label: u.name,
              }))}
              value={selectedUserToAdd || null}
              onChange={(v) => setSelectedUserToAdd(v ? String(v) : "")}
              placeholder={
                availableUsers.length === 0
                  ? "All users already assigned"
                  : "Select a user..."
              }
              disabled={availableUsers.length === 0}
              required
              data-testid="SEL_admin_role_user_select"
            />
            <FormControl.DatePicker
              label="Effective Date"
              value={effectiveDateToAdd}
              onChange={(e) => setEffectiveDateToAdd(e.target.value)}
              data-testid="INP_admin_role_user_effective_date"
            />
            <FormControl.Textarea
              label="Notes"
              value={notesToAdd}
              onChange={(e) => setNotesToAdd(e.target.value)}
              rows={3}
              placeholder="Reason for assignment, approver reference, etc."
              data-testid="INP_admin_role_user_notes"
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="submit"
            form="add-user-form"
            variant="fill"
            icon={Save}
            disabled={!selectedUserToAdd || submitting || permLoading || !canModify}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_admin_role_user_submit"
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default AdminRoleManagementLive;
